import { useQueryClient } from '@tanstack/react-query';
import { dequal } from 'dequal';
import * as React from 'react';
import { type PageData, pageQueryKey } from './useTablePagedQuery';

/**
 * Mirrors writes on per-id query keys into the matching page entry of a
 * paged-table provider.
 *
 * Other parts of the app (item panels, mutations, etc.) write fresh values
 * to canonical per-id keys like `commonQueryKeys.item(id)`. Without this
 * sync the table would keep showing the old accumulated value until remount.
 *
 * `matchKey` recognises a per-id key and returns the id; `transform` adapts
 * the per-id payload into the value shape the page entry stores, given the
 * entry's current cached value (so a transform can carry forward a field it
 * has no way to derive from this source, instead of blanking it). Returning
 * `undefined` from `transform` skips the update.
 */
export const useCrossCachePagesSync = <V, S = unknown>({
  providerName,
  instanceKey,
  pageIndexById,
  datasetSignature,
  matchKey,
  transform,
}: {
  providerName: string;
  instanceKey: string;
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  matchKey: (queryKey: readonly unknown[]) => number | null;
  transform: (id: number, sourceData: S, current: V | undefined) => V | undefined;
}) => {
  const queryClient = useQueryClient();
  const pageIndexByIdRef = React.useRef(pageIndexById);
  pageIndexByIdRef.current = pageIndexById;
  const datasetSignatureRef = React.useRef(datasetSignature);
  datasetSignatureRef.current = datasetSignature;

  React.useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      const id = matchKey(event.query.queryKey);
      if (id === null) return;
      const pageIndex = pageIndexByIdRef.current.get(id);
      if (pageIndex === undefined) return;

      const key = pageQueryKey(providerName, instanceKey, datasetSignatureRef.current, pageIndex);
      const current = queryClient.getQueryData<PageData<V>>(key);
      if (!current) return;

      const existingValue = current.valuesById.get(id);
      const value = transform(id, event.query.state.data as S, existingValue);
      if (value === undefined) return;
      if (dequal(existingValue, value)) return;

      const next = new Map(current.valuesById);
      next.set(id, value);
      queryClient.setQueryData<PageData<V>>(key, {
        valuesById: next,
        missingIds: current.missingIds,
      });
    });
  }, [queryClient, providerName, instanceKey, matchKey, transform]);
};
